import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { routesData } from '../data/routesData';

// Importez les images de bus
import busImage1 from '../assets/images/buses/bus-1.jpeg';
import busImage2 from '../assets/images/buses/bus-2.jpeg';
import busImage3 from '../assets/images/buses/bus-1.jpeg';

export default function Destinations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('ALL');
  
  // Tableau des images de bus locales
  const busImages = [busImage1, busImage2, busImage3];

  const filteredRoutes = useMemo(() => {
    if (filter === 'FROM_NDJ') {
      return routesData.filter((r) => r.departure === "N'Djaména");
    }

    if (filter === 'MORNING') {
      return routesData.filter((r) => {
        if (!r.departureTime) return true;
        const [h] = r.departureTime.split(':').map(Number);
        return h < 12;
      });
    }

    return routesData;
  }, [filter]);

  // Fonction pour naviguer vers la réservation
  const handleBook = (departure, arrival) => {
    const today = new Date().toISOString().split('T')[0];
    const params = new URLSearchParams({
      from: departure,
      to: arrival,
      date: today,
    });
    navigate(`/booking?${params.toString()}`);
  };

  const isActive = (name) =>
    filter === name
      ? 'bg-primary-600 text-white'
      : 'bg-transparent text-neutral-800 hover:bg-neutral-100';

  return (
    <div className="bg-neutral-50 py-16">
      <div className="container mx-auto px-4">
        {/* EN-TÊTE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-display font-bold text-neutral-900 mb-4">
            🗺️ {t('destinations_title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('destinations_subtitle')}
          </p>
        </motion.div>

        {/* FILTRES */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-full p-2 shadow-lg flex gap-2">
            <button
              type="button"
              onClick={() => setFilter('ALL')}
              className={`px-6 py-3 rounded-full font-semibold transition ${isActive('ALL')}`}
            >
              {t('all_routes')}
            </button>
            <button
              type="button"
              onClick={() => setFilter('FROM_NDJ')}
              className={`px-6 py-3 rounded-full font-semibold transition ${isActive('FROM_NDJ')}`}
            >
              {t('from_ndjamena')}
            </button>
            <button
              type="button"
              onClick={() => setFilter('MORNING')}
              className={`px-6 py-3 rounded-full font-semibold transition ${isActive('MORNING')}`}
            >
              {t('morning_departures')}
            </button>
          </div>
        </div>

        {/* GRILLE DES DESTINATIONS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredRoutes.map((route, i) => (
            <motion.div
              key={route.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, type: 'spring' }}
              whileHover={{ y: -8 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition cursor-pointer"
              onClick={() => handleBook(route.departure, route.arrival)}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={busImages[i % busImages.length]}
                  alt={`Bus ${route.departure} → ${route.arrival}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <span className="bg-accent-500 px-3 py-1 rounded-full text-sm font-bold">
                    {route.frequency || t('daily')}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-display font-bold text-2xl text-neutral-900 mb-1">
                      {route.departure} → {route.arrival}
                    </h3>
                    <p className="text-gray-500 flex items-center gap-2 text-sm">
                      🕐 <span>{route.duration || '—'}</span> •
                      📍 <span>{route.distance || '—'}</span>
                    </p>
                  </div>
                  {route.isPopular && (
                    <span className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs font-bold">
                      {t('popular')}
                    </span>
                  )}
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('starting_price')}</p>
                      <p className="text-3xl font-bold text-primary-600">
                        {(route.price ?? 0).toLocaleString()} <span className="text-lg">XAF</span>
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBook(route.departure, route.arrival);
                      }}
                      className="bg-primary-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-700 transition transform hover:scale-105"
                    >
                      {t('book')}
                    </button>
                  </div>
                </div>

                {/* Détails supplémentaires */}
                <div className="flex gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    💺 {t('capacity')}
                  </span>
                  <span className="flex items-center gap-1">❄️ {t('air_conditioned')}</span>
                  <span className="flex items-center gap-1">🚻 {t('toilettes')}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Message si aucun trajet */}
        {filteredRoutes.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-12"
          >
            <p className="text-red-500 text-lg">
              {t('no_routes_found')}
            </p>
            <button
              onClick={() => setFilter('ALL')}
              className="mt-4 text-primary-600 underline"
            >
              {t('reset_filters')}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}